import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Button,
  Box,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Schedule, CompareArrows, Settings, TrendingDown } from '@mui/icons-material';
import { daysUntil, formatRenewalDate, formatCurrency, countdownText, maskPolicyNumber, getUrgencyLevel, relativeTime } from '../../utils/renewalHelpers';
import t from '../../utils/renewalsTranslations';

const urgencyChipColor = (daysLeft) => {
  const level = getUrgencyLevel(daysLeft);
  if (level === 'expired' || level === 'critical') return 'error';
  if (level === 'urgent' || level === 'high') return 'warning';
  if (level === 'medium') return 'default';
  return 'default';
};

export default function RenewalCard({ policy, onCheckNow, checking, savingsAmount, lastCheckedAt }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const daysLeft = policy?.end_date ? daysUntil(policy.end_date) : null;
  const premium = policy?.premium_amount != null ? policy.premium_amount : 0;
  const runCheck = async () => {
    if (onCheckNow) {
      setLoading(true);
      try {
        await onCheckNow(policy.id);
      } finally {
        setLoading(false);
      }
    }
  };

  const isExpired = daysLeft != null && daysLeft < 0;
  const checkInProgress = loading || checking;

  return (
    <Card variant="outlined" sx={{ height: '100%', opacity: isExpired ? 0.85 : 1 }}>
      <CardActionArea onClick={() => navigate(`/renewals/${policy.id}`)} sx={{ flex: 1, alignItems: 'stretch' }} disabled={isExpired}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold">{policy.policy_name || maskPolicyNumber(policy.policy_number)}</Typography>
            {daysLeft != null && (
              <Chip
                size="small"
                icon={<Schedule />}
                label={daysLeft < 0 ? (t('renewals.errors.expired') || 'Expired') : countdownText(daysLeft)}
                color={urgencyChipColor(daysLeft)}
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">{policy.insurer_name} {policy.policy_type ? `• ${policy.policy_type}` : ''}</Typography>
          <Typography variant="body1" sx={{ mt: 0.5 }}>Current: {formatCurrency(premium)}</Typography>
          {savingsAmount != null && Number(savingsAmount) > 0 && (
            <Chip size="small" icon={<TrendingDown />} label={`Save ${formatCurrency(Math.round(Number(savingsAmount) / 10) * 10)}`} color="success" sx={{ mt: 1 }} />
          )}
          {lastCheckedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Last checked: {relativeTime(lastCheckedAt)}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
      <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={checkInProgress ? <CircularProgress size={16} /> : <CompareArrows />}
          onClick={(e) => { e.stopPropagation(); runCheck(); }}
          disabled={checkInProgress || isExpired}
        >
          {checkInProgress ? (t('renewals.errors.checkInProgress') || 'Checking...') : t('renewals.dashboard.checkNow')}
        </Button>
        <Button size="small" onClick={(e) => { e.stopPropagation(); navigate(`/renewals/${policy.id}`); }}>{t('renewals.card.viewDetails')}</Button>
        <Button size="small" startIcon={<Settings />} onClick={(e) => { e.stopPropagation(); navigate(`/renewals/${policy.id}/settings`); }}>
          {t('renewals.card.settings')}
        </Button>
      </Box>
    </Card>
  );
}
